/**
 * The block registry: lookup, the generated `BlockDocument` validator, tiptap node configs, the
 * insert palette, expression sites, plain text and Markdown export (plan §8.6).
 *
 * Owner: doc-engine (wave 1), page-editor (wave 2).
 */
import { z } from 'zod';
import { resolveText } from '../localized-text';
import type { ExprSite } from './expr-site';
import {
  compileContentExpression,
  matchesContent,
  tiptapAttrsFromSchema,
  type BlockCategory,
  type BlockDefinition,
  type MarkDefinition,
  type MarkdownContext,
  type TiptapAttributeSpec,
} from './block-kit';
import { blockDefinitions, markDefinitions } from './blocks';
import type { DocumentNode } from './node';

export const blockRegistry: ReadonlyMap<string, BlockDefinition> = new Map(
  blockDefinitions.map((b) => [b.name, b]),
);
export const markRegistry: ReadonlyMap<string, MarkDefinition> = new Map(
  markDefinitions.map((m) => [m.name, m]),
);

export function getBlock(name: string): BlockDefinition | undefined {
  return blockRegistry.get(name);
}

const groups = new Map<string, string[]>();
for (const block of blockDefinitions)
  for (const group of (block.group ?? '').split(/\s+/).filter(Boolean))
    groups.set(group, [...(groups.get(group) ?? []), block.name]);

// -------------------------------------------------------------------------------------------------
// Element ids
// -------------------------------------------------------------------------------------------------

/** The attribute every block of the `block` group may carry: a stable element id (page-editor, wave 2). */
export const BLOCK_ID_ATTR = 'id';
/** Element ids: short, URL-safe and unique within a document (the editor assigns them). */
export const BlockElementId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'Invalid block id');

const elementIdTypes = new Set(
  blockDefinitions
    .filter(
      (b) =>
        (b.group ?? '').split(/\s+/).includes('block') &&
        !(BLOCK_ID_ATTR in (b.attrs.shape as Record<string, unknown>)),
    )
    .map((b) => b.name),
);

/**
 * Block types that carry an element id in `attrs.id` besides their own attributes: every block of
 * the `block` group whose schema has no `id` of its own. Comments, presence, "Copy link to block",
 * drag and drop and version diffs address blocks by it; documents without ids stay valid.
 */
export function blockTypesWithElementId(): string[] {
  return [...elementIdTypes];
}

/** A node's attributes without its element id (what the block's own schema parses). */
export function ownAttrs(node: DocumentNode): Record<string, unknown> {
  const attrs = node.attrs ?? {};
  if (!(BLOCK_ID_ATTR in attrs) || !elementIdTypes.has(node.type)) return attrs;
  const { [BLOCK_ID_ATTR]: _id, ...rest } = attrs;
  return rest;
}

/** Does this block type carry an element id (`attrs.id`)? */
export function carriesElementId(type: string): boolean {
  return elementIdTypes.has(type);
}

const resolveName = (name: string) => (blockRegistry.has(name) ? [name] : (groups.get(name) ?? []));
const compiledContent = new Map<string, RegExp>();
for (const block of blockDefinitions)
  if (block.content !== undefined)
    compiledContent.set(block.name, compileContentExpression(block.content, resolveName));

/** Does a list of child types satisfy a block's content expression? */
export function contentAllows(blockName: string, childTypes: readonly string[]): boolean {
  const compiled = compiledContent.get(blockName);
  return compiled ? matchesContent(compiled, childTypes) : childTypes.length === 0;
}

const childTypeCache = new Map<string, ReadonlySet<string>>();
/** Every node type a block's content expression mentions (directly or through a group). */
export function allowedChildTypes(blockName: string): ReadonlySet<string> {
  let set = childTypeCache.get(blockName);
  if (!set) {
    const expr = blockRegistry.get(blockName)?.content ?? '';
    set = new Set((expr.match(/[A-Za-z][A-Za-z0-9_]*/g) ?? []).flatMap(resolveName));
    childTypeCache.set(blockName, set);
  }
  return set;
}

// -------------------------------------------------------------------------------------------------
// Validation
// -------------------------------------------------------------------------------------------------

/**
 * Bounds of one stored document. Sized for long documents (a 400-paragraph page with formatting,
 * a 60-page Word import: about 5,000 nodes and 150,000 characters) with headroom, and below the
 * API body limit (5 MB).
 */
export const DOCUMENT_LIMITS = {
  nodes: 20_000,
  depth: 20,
  childrenPerNode: 5_000,
  marksPerNode: 10,
  textLength: 500_000,
  jsonBytes: 2_000_000,
} as const;

/** Parse attributes; the result for absent or empty attributes is computed once per definition. */
type Parsed =
  | { success: true; data: unknown; error?: undefined }
  | { success: false; data?: undefined; error: z.ZodError };
const emptyParse = new WeakMap<object, Parsed>();
function parseAttrs(def: { attrs: z.ZodType }, attrs: Record<string, unknown> | undefined): Parsed {
  if (attrs === undefined || (attrs && typeof attrs === 'object' && Object.keys(attrs).length === 0)) {
    let cached = emptyParse.get(def);
    if (!cached) {
      cached = def.attrs.safeParse({}) as Parsed;
      emptyParse.set(def, cached);
    }
    return cached;
  }
  return def.attrs.safeParse(attrs) as Parsed;
}

export type DocumentIssue = { path: (string | number)[]; message: string; code: string };

/** Validate a block document against the registry; an empty list means valid. */
export function validateBlockDocument(value: unknown): DocumentIssue[] {
  const issues: DocumentIssue[] = [];
  const fail = (path: (string | number)[], message: string, code: string) =>
    issues.push({ path, message, code });
  const root = value as DocumentNode;
  if (!root || typeof root !== 'object' || root.type !== 'doc') {
    fail([], 'A document root is required', 'doc.root');
    return issues;
  }
  let encoded: string;
  try {
    encoded = JSON.stringify(root);
  } catch {
    fail([], 'Invalid document', 'doc.invalid');
    return issues;
  }
  if (encoded.length > DOCUMENT_LIMITS.jsonBytes) {
    fail([], 'Document is too large', 'doc.too_large');
    return issues;
  }
  let count = 0,
    textLength = 0;
  const visit = (
    node: DocumentNode,
    depth: number,
    parent: DocumentNode | null,
    path: (string | number)[],
  ): void => {
    if (++count > DOCUMENT_LIMITS.nodes || depth > DOCUMENT_LIMITS.depth) {
      fail(path, 'Document is too complex', 'doc.too_complex');
      return;
    }
    const block = node && typeof node === 'object' ? blockRegistry.get(node.type) : undefined;
    if (!block || (node.type === 'doc' && parent)) {
      fail(path, 'Unsupported block type', 'doc.unsupported_block');
      return;
    }
    if (node.text !== undefined) {
      if (typeof node.text !== 'string') fail(path, 'Invalid text', 'doc.text');
      else textLength += node.text.length;
    }
    if (node.type === 'text' && typeof node.text !== 'string') fail(path, 'Text nodes need text', 'doc.text');
    if (
      node.attrs !== undefined &&
      (!node.attrs || typeof node.attrs !== 'object' || Array.isArray(node.attrs))
    ) {
      fail(path, 'Invalid block attributes', 'doc.attrs');
      return;
    }
    if (
      node.content !== undefined &&
      (!Array.isArray(node.content) || node.content.length > DOCUMENT_LIMITS.childrenPerNode)
    ) {
      fail(path, 'Invalid block content', 'doc.content');
      return;
    }
    let attrs = node.attrs;
    if (attrs && BLOCK_ID_ATTR in attrs && elementIdTypes.has(node.type)) {
      const { [BLOCK_ID_ATTR]: id, ...rest } = attrs;
      if (id !== null && id !== undefined && !BlockElementId.safeParse(id).success)
        fail([...path, 'attrs', BLOCK_ID_ATTR], 'Invalid block id', 'doc.block_id');
      attrs = rest;
    }
    const parsed = parseAttrs(block, attrs);
    const ctx = {
      parent,
      path,
      fail: (message: string, code = `${node.type}.invalid`) => fail(path, message, code),
    };
    // Structural hooks see well-formed nodes only: an array of children and parsed attributes.
    // The registry holds blocks of different attribute types: `parsed` was produced by this block's own schema.
    if (block.validate && parsed.success) block.validate(node, parsed.data as never, ctx);
    if (!parsed.success)
      for (const issue of parsed.error.issues.slice(0, 5))
        fail(
          [...path, 'attrs', ...issue.path.map((p) => (typeof p === 'symbol' ? String(p) : p))],
          issue.code === 'custom' ? issue.message : `Invalid ${node.type} configuration: ${issue.message}`,
          `${node.type}.attrs`,
        );
    if (node.marks !== undefined) {
      if (!Array.isArray(node.marks) || node.marks.length > DOCUMENT_LIMITS.marksPerNode) {
        fail(path, 'Invalid formatting', 'doc.marks');
        return;
      }
      const allowed = parent ? blockRegistry.get(parent.type)?.marks : undefined;
      for (const mark of node.marks) {
        const def = mark && typeof mark === 'object' ? markRegistry.get(mark.type) : undefined;
        if (!def || allowed === '' || !parseAttrs(def, mark.attrs).success)
          fail([...path, 'marks'], 'Invalid formatting or link', 'doc.mark');
      }
    }
    if (node.content !== undefined) {
      const children = node.content;
      if (children.some((child) => !child || typeof child !== 'object' || typeof child.type !== 'string'))
        fail(path, block.contentMessage ?? 'Invalid nested block', `${node.type}.content`);
      else if (!children.every((child) => blockRegistry.has(child.type))) {
        // The unknown child reports itself below.
      } else if (
        !contentAllows(
          node.type,
          children.map((c) => c.type),
        )
      )
        fail(path, block.contentMessage ?? 'Invalid nested block', `${node.type}.content`);
      children.forEach((child, i) => {
        if (child && typeof child === 'object') visit(child, depth + 1, node, [...path, 'content', i]);
      });
    } else if (!contentAllows(node.type, []) && compiledContent.has(node.type) && node.type !== 'doc') {
      // A container without children: allowed only when its expression accepts no children.
      fail(path, block.contentMessage ?? 'Invalid nested block', `${node.type}.content`);
    }
  };
  visit(root, 0, null, []);
  // Canvas element and frame identifiers are unique within a document.
  const ids = new Set<string>();
  for (const frame of (Array.isArray(root.content) ? root.content : []).filter(
    (n) => n?.type === 'canvasPage',
  ))
    for (const node of [frame, ...(Array.isArray(frame.content) ? frame.content : [])]) {
      const id = String(node?.attrs?.id);
      if (ids.has(id)) fail([], 'Canvas identifiers must be unique', 'canvas.ids');
      ids.add(id);
    }
  if (textLength > DOCUMENT_LIMITS.textLength) fail([], 'Document text is too long', 'doc.text_length');
  return issues;
}

/** Does a shipped editor open this node type (see `BlockDefinition.supported`)? */
export function isSupportedBlock(name: string): boolean {
  return blockRegistry.get(name)?.supported === true;
}
/** Does a shipped editor keep this mark? */
export function isSupportedMark(name: string): boolean {
  return markRegistry.get(name)?.supported === true;
}

/**
 * Nodes and marks of a valid document that no shipped editor opens yet (model-only blocks such as
 * tables, toggles with a summary, mentions or barcodes), with their paths.
 */
export function unsupportedContent(doc: DocumentNode): DocumentIssue[] {
  const issues: DocumentIssue[] = [];
  const visit = (node: DocumentNode, path: (string | number)[]) => {
    if (!node || typeof node !== 'object') return;
    if (!isSupportedBlock(node.type))
      issues.push({
        path,
        message: `${node.type} is not available in the editor yet`,
        code: 'doc.unsupported_in_editor',
      });
    for (const mark of node.marks ?? [])
      if (mark && !isSupportedMark(mark.type))
        issues.push({
          path: [...path, 'marks'],
          message: `${mark.type} is not available in the editor yet`,
          code: 'doc.unsupported_in_editor',
        });
    node.content?.forEach((child, i) => visit(child, [...path, 'content', i]));
  };
  visit(doc, []);
  return issues;
}

const documentSchema = (editorOnly: boolean) =>
  z.custom<DocumentNode>().superRefine((value, ctx) => {
    const issues = validateBlockDocument(value);
    if (!issues.length && editorOnly) issues.push(...unsupportedContent(value));
    for (const issue of issues)
      ctx.addIssue({
        code: 'custom',
        message: issue.message,
        path: issue.path,
        params: { code: issue.code },
      });
  });

/**
 * A block document the shipped editors can open: bounded JSON for pages, notes, mail, comments
 * and layouts, generated from the registry. Every API that stores a document uses it, so no
 * editor ever loads content it would drop. New blocks join when their editor support lands
 * (`supported: true`).
 */
export const BlockDocument = documentSchema(true);
export type BlockDocument = DocumentNode;

/** Any valid block document of the model, including model-only blocks (conversion, rendering). */
export const ModelBlockDocument = documentSchema(false);

/**
 * Attribute schemas by block, as published to AI authoring (the blocks of the shipped editor and
 * the canvas v1 nodes; model-only blocks join when their editor support lands).
 */
export const DocumentBlockAttributes: Record<string, z.ZodType> = Object.fromEntries(
  [
    'doc',
    'visual',
    'canvasPage',
    'canvasItem',
    'calculationTable',
    'recordValue',
    'dataView',
    'image',
    'form',
    'booking',
    'customComponent',
    'recordFields',
    'section',
    'toggle',
    'spacer',
    'dataResult',
    'uiBlock',
    'tabs',
    'tab',
    'columns',
  ].map((name) => [name, blockRegistry.get(name)!.attrs]),
);

// -------------------------------------------------------------------------------------------------
// tiptap
// -------------------------------------------------------------------------------------------------

export type TiptapNodeConfig = {
  name: string;
  group?: string;
  content?: string;
  inline?: boolean;
  atom?: boolean;
  marks?: string;
  attributes: Record<string, TiptapAttributeSpec>;
};

/** The registry part of a tiptap `Node.create` config: spread it and add views and commands. */
export function tiptapNodeConfig(name: string): TiptapNodeConfig {
  const block = blockRegistry.get(name);
  if (!block) throw new Error(`Unknown block "${name}"`);
  return {
    name: block.name,
    ...(block.group ? { group: block.group } : {}),
    ...(block.content !== undefined ? { content: block.content } : {}),
    ...(block.inline ? { inline: true } : {}),
    ...(block.atom ? { atom: true } : {}),
    ...(block.marks !== undefined ? { marks: block.marks } : {}),
    attributes: tiptapAttrsFromSchema(block),
  };
}

export function tiptapMarkAttributes(name: string): Record<string, TiptapAttributeSpec> {
  const mark = markRegistry.get(name);
  if (!mark) throw new Error(`Unknown mark "${name}"`);
  return tiptapAttrsFromSchema(mark);
}

export type AttributeDrift = { name: string; missing: string[]; extra: string[] };

/**
 * Compare the attribute names of a tiptap node or mark (as an editor declares them) with the
 * registry's Zod attributes. `missing` = in the schema but not in the editor (lost on edit),
 * `extra` = in the editor but not in the schema (rejected on save). Editors run this in a test
 * over their extensions; an empty result for every extension means they cannot drift.
 */
export function tiptapAttributeDrift(name: string, editorAttributes: readonly string[]): AttributeDrift {
  const def = blockRegistry.get(name) ?? markRegistry.get(name);
  if (!def) return { name, missing: [], extra: [...editorAttributes] };
  const schemaKeys = Object.keys(def.attrs.shape);
  return {
    name,
    missing: schemaKeys.filter((k) => !editorAttributes.includes(k)),
    extra: editorAttributes.filter((k) => !schemaKeys.includes(k)),
  };
}

// -------------------------------------------------------------------------------------------------
// Insert palette (legacy `builderElements` shape)
// -------------------------------------------------------------------------------------------------

const CATEGORY_LABELS: Record<BlockCategory, { en: string; de: string }> = {
  writing: { en: 'Writing', de: 'Schreiben' },
  layout: { en: 'Layout', de: 'Layout' },
  media: { en: 'Media', de: 'Medien' },
  visuals: { en: 'Visuals', de: 'Visualisierungen' },
  navigation: { en: 'Navigation', de: 'Navigation' },
  data: { en: 'Data', de: 'Daten' },
  interface: { en: 'Interface', de: 'Oberfläche' },
};

export function blockCategoryLabel(category: BlockCategory, locale = 'en'): string {
  return resolveText(CATEGORY_LABELS[category], locale);
}

export type PaletteEntry = {
  type: string;
  label: string;
  category: string;
  description: string;
  compact?: true;
  recordInterface?: true;
};

/** Blocks offered by the insert menu, in order, with labels in one locale. */
export function paletteEntries(locale = 'en'): PaletteEntry[] {
  return blockDefinitions
    .filter((b) => b.palette)
    .sort((a, b) => a.palette!.order - b.palette!.order)
    .map((b) => ({
      type: b.name,
      label: resolveText(b.label, locale),
      category: b.category ? blockCategoryLabel(b.category, locale) : '',
      description: b.description ? resolveText(b.description, locale) : '',
      ...(b.palette!.compact ? { compact: true as const } : {}),
      ...(b.palette!.recordInterface ? { recordInterface: true as const } : {}),
    }));
}

// -------------------------------------------------------------------------------------------------
// Walkers: expression sites, plain text, Markdown
// -------------------------------------------------------------------------------------------------

/** Every expression of a document with its path (`['content', 2, 'attrs', 'visibleWhen']`). */
export function documentExprSites(doc: DocumentNode): ExprSite[] {
  const out: ExprSite[] = [];
  const visit = (node: DocumentNode, path: (string | number)[]) => {
    const block = blockRegistry.get(node.type);
    if (block?.expressions) {
      const parsed = block.attrs.safeParse(ownAttrs(node));
      if (parsed.success)
        // `parsed` came from this block's own schema (the registry erases the attribute type).
        for (const site of block.expressions(parsed.data as never))
          out.push({ ...site, path: [...path, 'attrs', ...site.path] });
    }
    node.content?.forEach((child, i) => child && visit(child, [...path, 'content', i]));
  };
  visit(doc, []);
  return out;
}

const INLINE_BREAK_TYPES = new Set(['hardBreak']);

/** Plain text of one node (blocks on lines, tables as `a | b`), without trimming. */
export function nodeText(node: DocumentNode): string {
  if (node.type === 'text') return node.text ?? '';
  if (INLINE_BREAK_TYPES.has(node.type)) return '\n';
  const block = blockRegistry.get(node.type);
  if (block?.toText) return block.toText(node, { children: childrenText });
  return childrenText(node);
}

function childrenText(node: DocumentNode): string {
  const block = blockRegistry.get(node.type);
  const children = node.content ?? [];
  if (block?.content && /inline|text/.test(block.content) && !/block/.test(block.content))
    return children.map(nodeText).join('');
  return children
    .map(nodeText)
    .filter((t) => t.trim() !== '')
    .join('\n');
}

/** Plain text of a document for AI sources and search: blocks on lines, tables as `a | b`. */
export function blocksToText(doc: DocumentNode): string {
  return nodeText(doc)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function escapeMarkdown(text: string): string {
  return text
    .replace(/([\\`*_[\]#<>|~])/g, '\\$1')
    .replace(/^(\d+)\. /gm, '$1\\. ')
    .replace(/^([-+]) /gm, '\\$1 ');
}

/** Markdown of a document through the registry (`toMarkdown` per block; GFM tables and task lists). */
export function blocksToMarkdown(doc: DocumentNode): string {
  const inline = (node: DocumentNode): string => {
    let out = '';
    for (const child of node.content ?? []) {
      if (child.type === 'hardBreak') {
        out += '  \n';
        continue;
      }
      if (child.type !== 'text') {
        const block = blockRegistry.get(child.type);
        out += block?.toMarkdown
          ? block.toMarkdown(child, ctx)
          : (block?.toText?.(child, { children: () => '' }) ?? '');
        continue;
      }
      const isCode = child.marks?.some((m) => m.type === 'code');
      let text = isCode ? (child.text ?? '') : escapeMarkdown(child.text ?? '');
      const marks = [...(child.marks ?? [])].sort(
        (a, b) => (a.type === 'link' ? 1 : 0) - (b.type === 'link' ? 1 : 0),
      );
      for (const mark of marks) {
        const def = markRegistry.get(mark.type)?.markdown;
        if (!def) continue;
        // The mark's own markdown writer reads its own attribute type (the registry erases it).
        const attrs = (mark.attrs ?? {}) as never;
        const open = typeof def.open === 'function' ? def.open(attrs) : def.open;
        const close = typeof def.close === 'function' ? def.close(attrs) : def.close;
        text = `${open}${text}${close}`;
      }
      out += text;
    }
    return out;
  };
  const blocks = (nodes: readonly DocumentNode[] | undefined): string =>
    (nodes ?? [])
      .map((node) => {
        const block = blockRegistry.get(node.type);
        if (block?.toMarkdown) return block.toMarkdown(node, ctx);
        if (block?.content && /block/.test(block.content)) return blocks(node.content);
        if (block?.content) return inline(node);
        return block?.toText ? block.toText(node, { children: () => '' }) : '';
      })
      .filter((s) => s.trim() !== '')
      .join('\n\n');
  const ctx: MarkdownContext = { inline, blocks, text: nodeText };
  if (doc.type !== 'doc') return blocks([doc]);
  return `${blocks(doc.content)}\n`;
}
